/**
 * WBBL — World Blockchain Basketball League
 * Red / White / Navy — classic American pro sports palette.
 * Primary red  : #C8102E  (NBA / NFL red)
 * Primary navy : #002868  (patriotic navy)
 * White        : #F5F8FF
 * Accent       : #E8C84A  (championship gold, used sparingly)
 */
export const DESKTOP_CSS = `
@import url("https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Rajdhani:wght@500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap");

:root {
	/* ── backgrounds (navy-dark spectrum) ── */
	--bg-0: #060810;
	--bg-1: #0b0e1a;
	--bg-2: #111526;
	--bg-3: #181d30;
	--bg-4: #1f2640;

	/* ── borders ── */
	--line: #2a3560;
	--line-hot: #3d4f88;

	/* ── type ── */
	--text: #eef1fb;
	--text-2: #9daac8;
	--text-3: #5e6f98;

	/* ── brand ── */
	--red: #C8102E;
	--red-2: #e8273f;
	--red-deep: #9a0c22;
	--navy: #002868;
	--navy-2: #003399;
	--white: #F5F8FF;

	/* ── semantic accents ── */
	--gold: #E8C84A;
	--gold-2: #f0d86a;
	--green: #2fd67b;
	--blue: #4aa3ff;

	/* ── gradients ── */
	--metal: linear-gradient(180deg, #1e2645 0%, #0e1228 48%, #080c1e 100%);
	--metal-bright: linear-gradient(180deg, #273060 0%, #15213e 55%, #0d1630 100%);
	--panel: linear-gradient(180deg, #131828 0%, #0a0d1c 100%);
	--red-btn: linear-gradient(180deg, #e83248 0%, #c8102e 45%, #9e0c25 100%);
	--navy-btn: linear-gradient(180deg, #1b3b8a 0%, #002868 45%, #001845 100%);
	--green-btn: linear-gradient(180deg, #56e895 0%, #1faf5d 55%, #178a48 100%);
	--steel-btn: linear-gradient(180deg, #2a3a60 0%, #162040 55%, #0d1630 100%);

	/* ── layout ── */
	--sidebar-w: 232px;
	--top-h: 52px;
	--font-display: "Barlow Condensed", "Arial Narrow", sans-serif;
	--font-ui: "Rajdhani", "Segoe UI", sans-serif;
	--font-body: "Source Sans 3", "Segoe UI", sans-serif;
	--radius: 3px;
	--glow-red: 0 0 18px rgba(200, 16, 46, 0.40);
	--glow-navy: 0 0 18px rgba(0, 40, 104, 0.45);
}

*, *::before, *::after { box-sizing: border-box; }

html, body, #root {
	height: 100%;
	margin: 0;
	background: var(--bg-0);
	color: var(--text);
	font-family: var(--font-body);
	font-size: 14px;
	overflow: hidden;
}

button, input, select { font: inherit; color: inherit; }
button { cursor: pointer; border: none; background: none; }
a { color: var(--red-2); text-decoration: none; }
a:hover { color: var(--white); }

/* ===== SHELL ===== */
.app-shell {
	display: grid;
	grid-template-rows: var(--top-h) 1fr;
	height: 100%;
	background:
		radial-gradient(1200px 500px at 80% -10%, rgba(200,16,46,0.07), transparent 50%),
		radial-gradient(900px 400px at 10% 110%, rgba(0,40,104,0.10), transparent 50%),
		var(--bg-0);
}

/* ── topbar ── */
.topbar {
	display: grid;
	grid-template-columns: 260px 1fr auto;
	align-items: center;
	gap: 16px;
	padding: 0 14px;
	background: var(--metal);
	border-bottom: 2px solid var(--red-deep);
	box-shadow:
		inset 0 1px 0 rgba(255,255,255,0.10),
		0 4px 18px rgba(0,0,0,0.60);
	z-index: 20;
	position: relative;
}

.topbar::after {
	content: "";
	position: absolute;
	left: 0; right: 0; bottom: -4px;
	height: 2px;
	background: linear-gradient(90deg, transparent, var(--gold), transparent);
	opacity: 0.30;
}

.topbar-brand {
	display: flex;
	align-items: baseline;
	gap: 8px;
	font-family: var(--font-display);
	font-weight: 800;
	font-size: 26px;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	line-height: 1;
}
.topbar-brand .mark {
	color: var(--red-2);
	text-shadow: var(--glow-red);
}
.topbar-brand .rest { color: var(--white); }
.topbar-brand .ver {
	font-size: 11px;
	letter-spacing: 0.14em;
	color: var(--text-3);
	font-family: var(--font-ui);
	font-weight: 700;
}

.topbar-league {
	justify-self: center;
	font-family: var(--font-display);
	font-size: 20px;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--white);
	text-shadow: 0 1px 0 #000;
}

.topbar-meta {
	display: flex;
	align-items: center;
	gap: 14px;
	justify-self: end;
}
.meta-chip {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	line-height: 1.15;
}
.meta-chip .label {
	font-family: var(--font-ui);
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--text-3);
}
.meta-chip .value {
	font-family: var(--font-display);
	font-size: 16px;
	font-weight: 700;
	color: var(--text);
}

.icon-btn {
	width: 34px;
	height: 34px;
	display: grid;
	place-items: center;
	border-radius: var(--radius);
	background: var(--steel-btn);
	border: 1px solid var(--line);
	color: var(--text-2);
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
}
.icon-btn:hover {
	color: #fff;
	border-color: var(--red-2);
}

.workspace {
	display: grid;
	grid-template-columns: var(--sidebar-w) 1fr;
	min-height: 0;
}

/* ===== SIDEBAR ===== */
.sidebar {
	background:
		linear-gradient(180deg, rgba(0,40,104,0.18), transparent 40%),
		repeating-linear-gradient(
			-12deg,
			rgba(255,255,255,0.012) 0 2px,
			transparent 2px 6px
		),
		linear-gradient(180deg, #0c1020 0%, #060810 100%);
	border-right: 1px solid rgba(0,40,104,0.4);
	box-shadow: inset -1px 0 0 rgba(255,255,255,0.03);
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: auto;
}

.sim-block {
	padding: 14px 12px 12px;
	border-bottom: 1px solid rgba(0,0,0,0.6);
	background: var(--metal-bright);
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.1);
}
.sim-block h2 {
	margin: 0 0 10px;
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 800;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--white);
	text-shadow: 0 2px 0 #000;
}

.sim-actions {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}

/* ── buttons ── */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	min-height: 38px;
	padding: 8px 12px;
	border-radius: var(--radius);
	font-family: var(--font-display);
	font-weight: 800;
	font-size: 17px;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	border: 1px solid transparent;
	box-shadow:
		inset 0 1px 0 rgba(255,255,255,0.20),
		0 2px 0 rgba(0,0,0,0.55);
	transition: filter 0.12s ease, transform 0.08s ease;
}
.btn:hover:not(:disabled) { filter: brightness(1.10); }
.btn:active:not(:disabled) { transform: translateY(1px); }
.btn:disabled { opacity: 0.40; cursor: not-allowed; }

.btn-primary {
	background: var(--red-btn);
	color: var(--white);
	border-color: var(--red-deep);
}
.btn-secondary {
	background: var(--navy-btn);
	color: var(--white);
	border-color: #001440;
}
.btn-green {
	background: var(--green-btn);
	color: #04140b;
	border-color: #0f6a36;
}
.btn-ghost {
	background: transparent;
	border: 1px solid var(--line);
	color: var(--text-2);
	box-shadow: none;
}

.status-bar {
	margin-top: 10px;
	padding: 8px 10px;
	border-radius: var(--radius);
	background: #060810;
	border: 1px solid var(--line);
	color: var(--text-2);
	font-family: var(--font-ui);
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.03em;
}
.status-bar.error {
	border-color: var(--red);
	color: #ffb4b4;
	background: rgba(200,16,46,0.10);
}

/* ── nav ── */
.nav-section { padding: 12px 0 6px; }
.nav-section-title {
	padding: 0 14px 8px;
	font-family: var(--font-ui);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.18em;
	text-transform: uppercase;
	color: var(--text-3);
}

.nav-item {
	width: 100%;
	display: grid;
	grid-template-columns: 28px 1fr;
	align-items: center;
	gap: 10px;
	padding: 9px 14px;
	color: var(--text-2);
	text-align: left;
	border-left: 3px solid transparent;
	font-family: var(--font-ui);
	font-size: 16px;
	font-weight: 600;
	letter-spacing: 0.03em;
}
.nav-item:hover {
	background: rgba(255,255,255,0.04);
	color: var(--white);
}
.nav-item.active {
	background: linear-gradient(90deg, rgba(200,16,46,0.20), transparent 80%);
	color: var(--white);
	border-left-color: var(--red);
	text-shadow: 0 0 10px rgba(200,16,46,0.30);
}
.nav-item .glyph {
	width: 28px;
	height: 22px;
	display: grid;
	place-items: center;
	border-radius: 2px;
	background: rgba(0,0,0,0.4);
	border: 1px solid var(--line);
	font-family: var(--font-display);
	font-size: 11px;
	font-weight: 800;
	letter-spacing: 0.04em;
	color: var(--gold);
}

/* ===== MAIN ===== */
.main {
	min-width: 0;
	min-height: 0;
	overflow: auto;
	padding: 18px 20px 32px;
	background:
		linear-gradient(180deg, rgba(0,40,104,0.04), transparent 120px),
		var(--bg-1);
}

.page-title {
	margin: 0 0 2px;
	font-family: var(--font-display);
	font-size: 38px;
	font-weight: 800;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: var(--white);
	text-shadow: 0 2px 0 #000;
}
.page-sub {
	margin: 0 0 18px;
	color: var(--text-2);
	font-size: 14px;
	max-width: 70ch;
}

/* ── panel / card ── */
.panel {
	background: var(--panel);
	border: 1px solid rgba(0,0,0,0.7);
	border-radius: 4px;
	box-shadow:
		inset 0 1px 0 rgba(255,255,255,0.05),
		0 8px 24px rgba(0,0,0,0.35);
	overflow: hidden;
	position: relative;
}
.panel::before {
	content: "";
	position: absolute;
	inset: 0;
	border: 1px solid rgba(255,255,255,0.03);
	border-radius: 4px;
	pointer-events: none;
}
.panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 14px;
	background: var(--metal);
	border-bottom: 1px solid rgba(0,0,0,0.6);
	font-family: var(--font-display);
	font-size: 17px;
	font-weight: 800;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--white);
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
}
.panel-body { padding: 12px 14px; }

.grid-2 {
	display: grid;
	grid-template-columns: 1.25fr 1fr;
	gap: 14px;
}
.grid-3 {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 14px;
}

/* ── stat cards ── */
.stat-cards {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 12px;
	margin-bottom: 16px;
}
.stat-card {
	position: relative;
	padding: 14px 14px 12px;
	border-radius: 4px;
	background: linear-gradient(160deg, #161e38 0%, #0c1020 100%);
	border: 1px solid rgba(0,0,0,0.6);
	box-shadow:
		inset 0 1px 0 rgba(255,255,255,0.07),
		0 6px 16px rgba(0,0,0,0.3);
	overflow: hidden;
}
.stat-card::after {
	content: "";
	position: absolute;
	top: 0; left: 0; right: 0;
	height: 3px;
	background: linear-gradient(90deg, var(--red), var(--navy-2), transparent);
}
.stat-card .label {
	font-family: var(--font-ui);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--text-3);
}
.stat-card .value {
	margin-top: 6px;
	font-family: var(--font-display);
	font-size: 34px;
	font-weight: 800;
	letter-spacing: 0.02em;
	color: var(--white);
	text-shadow: 0 2px 0 #000;
	line-height: 1;
}

/* ── tables ── */
.data-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.data-table th {
	position: sticky;
	top: 0;
	background: #0d1328;
	color: var(--gold);
	font-family: var(--font-display);
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	text-align: left;
	padding: 9px 10px;
	border-bottom: 1px solid var(--line-hot);
	white-space: nowrap;
	z-index: 1;
}
.data-table td {
	padding: 8px 10px;
	border-bottom: 1px solid rgba(42,53,96,0.55);
	color: var(--text);
}
.data-table tr:nth-child(even) td { background: rgba(255,255,255,0.02); }
.data-table tr:hover td { background: rgba(200,16,46,0.07); }
.data-table .num {
	text-align: right;
	font-variant-numeric: tabular-nums;
	font-family: var(--font-ui);
	font-weight: 600;
}
.data-table .team-user {
	color: var(--red-2);
	font-weight: 700;
}

/* ── notifications ── */
.notif-wrap { position: relative; }
.notif-bell { position: relative; }
.notif-badge {
	position: absolute;
	top: -4px; right: -4px;
	min-width: 18px;
	height: 18px;
	padding: 0 5px;
	border-radius: 999px;
	background: var(--red);
	color: #fff;
	font-family: var(--font-ui);
	font-size: 11px;
	font-weight: 800;
	line-height: 18px;
	text-align: center;
	box-shadow: 0 0 0 2px #0b0e1a;
	pointer-events: none;
}
.notif-panel {
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	width: min(380px, 92vw);
	max-height: 420px;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	background: var(--panel);
	border: 1px solid rgba(0,0,0,0.7);
	border-radius: 4px;
	box-shadow: 0 12px 32px rgba(0,0,0,0.55);
	z-index: 40;
}
.notif-panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 12px;
	background: var(--metal);
	border-bottom: 1px solid rgba(0,0,0,0.6);
	font-family: var(--font-display);
	font-size: 15px;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--white);
}
.notif-list {
	list-style: none;
	margin: 0; padding: 0;
	overflow: auto;
	max-height: 360px;
}
.notif-list li {
	padding: 10px 12px;
	border-bottom: 1px solid rgba(42,53,96,0.55);
	font-size: 13px;
	line-height: 1.35;
	color: var(--text);
}
.notif-list li.error {
	color: #ffb4b4;
	background: rgba(200,16,46,0.10);
}
.notif-list li:hover { background: rgba(200,16,46,0.06); }

/* ── player / team links ── */
.player-link, .team-link {
	padding: 0;
	background: none;
	border: none;
	cursor: pointer;
	color: var(--text);
	font: inherit;
	font-weight: 600;
	text-align: left;
	border-bottom: 1px dotted var(--line-hot);
}
.player-link:hover, .team-link:hover {
	color: var(--red-2);
	border-bottom-color: var(--red);
}

/* ===== COLLEGE MADNESS BRACKET ===== */
.madness-wrap { display: flex; flex-direction: column; gap: 14px; }
.madness-ff {
	padding: 10px 12px;
	border: 1px solid rgba(200,16,46,0.22);
	border-radius: 8px;
	background: rgba(200,16,46,0.05);
}
.madness-ff-games { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
.madness-bracket {
	display: flex;
	gap: 12px;
	overflow-x: auto;
	padding-bottom: 8px;
	align-items: stretch;
}
.madness-round {
	min-width: 168px;
	flex: 0 0 auto;
	display: flex;
	flex-direction: column;
}
.madness-round-title {
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--red-2);
	margin-bottom: 8px;
	text-align: center;
}
.madness-round-games {
	display: flex;
	flex-direction: column;
	justify-content: space-around;
	gap: 10px;
	flex: 1;
}
.madness-matchup {
	border: 1px solid rgba(255,255,255,0.10);
	border-radius: 6px;
	overflow: hidden;
	background: rgba(0,0,0,0.2);
}
.madness-slot {
	display: grid;
	grid-template-columns: 22px 1fr 28px;
	gap: 4px;
	align-items: center;
	padding: 5px 8px;
	font-size: 12px;
	border-bottom: 1px solid rgba(255,255,255,0.05);
}
.madness-slot:last-child { border-bottom: none; }
.madness-slot-won { background: rgba(47,214,123,0.10); font-weight: 600; }
.madness-seed { color: rgba(255,255,255,0.40); font-size: 11px; text-align: right; }
.madness-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.madness-score { text-align: right; font-variant-numeric: tabular-nums; }
.madness-champion { text-align: center; padding: 10px; font-size: 15px; color: var(--gold); }

/* ── pill badges ── */
.pill {
	display: inline-flex;
	align-items: center;
	padding: 3px 9px;
	border-radius: 999px;
	background: rgba(200,16,46,0.15);
	border: 1px solid rgba(200,16,46,0.35);
	color: var(--red-2);
	font-family: var(--font-ui);
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.1em;
	text-transform: uppercase;
}

.college-banner {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 16px;
	margin-bottom: 14px;
	border-radius: 4px;
	background:
		linear-gradient(90deg, rgba(0,40,104,0.22), transparent 70%),
		var(--bg-2);
	border: 1px solid var(--line);
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
}

.muted { color: var(--text-3); }
.empty { padding: 28px; text-align: center; color: var(--text-2); }

/* ===== HOME / BOOT ===== */
.home-hero {
	min-height: 100%;
	display: grid;
	place-items: center;
	padding: 36px 20px;
	background:
		radial-gradient(ellipse 80% 50% at 50% -10%, rgba(200,16,46,0.16), transparent 55%),
		radial-gradient(ellipse 60% 40% at 90% 80%, rgba(0,40,104,0.18), transparent 50%),
		radial-gradient(ellipse 50% 30% at 0% 50%, rgba(0,51,153,0.10), transparent 50%),
		linear-gradient(165deg, #06080e 0%, #0c1020 45%, #060810 100%);
}

.home-card {
	width: min(980px, 100%);
	border-radius: 6px;
	overflow: hidden;
	background: var(--panel);
	border: 1px solid rgba(0,0,0,0.7);
	box-shadow:
		0 0 0 1px rgba(255,255,255,0.04),
		0 24px 60px rgba(0,0,0,0.55),
		var(--glow-red);
}

.home-card-header {
	padding: 30px 34px 22px;
	background:
		linear-gradient(180deg, rgba(200,16,46,0.14), transparent 55%),
		linear-gradient(180deg, rgba(0,40,104,0.08), transparent 100%),
		var(--metal);
	border-bottom: 2px solid var(--red-deep);
	position: relative;
}
.home-card-header::after {
	content: "";
	position: absolute;
	left: 0; right: 0; bottom: 0;
	height: 1px;
	background: linear-gradient(90deg, var(--red), var(--navy), transparent);
}
.home-card-header h1 {
	margin: 0;
	font-family: var(--font-display);
	font-size: 64px;
	font-weight: 800;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	line-height: 0.92;
	color: var(--white);
}
.home-card-header h1 em {
	font-style: normal;
	color: var(--red-2);
	text-shadow: var(--glow-red);
}
.home-card-header p {
	margin: 12px 0 0;
	color: var(--text-2);
	max-width: 52ch;
	font-size: 15px;
	line-height: 1.45;
}

.home-card-body {
	padding: 24px 34px 30px;
	display: grid;
	grid-template-columns: 1.1fr 0.9fr;
	gap: 28px;
}

.field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 14px;
}
.field label {
	font-family: var(--font-ui);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--text-3);
}
.field input, .field select {
	background: #060810;
	border: 1px solid var(--line);
	border-radius: var(--radius);
	padding: 11px 12px;
	box-shadow: inset 0 2px 6px rgba(0,0,0,0.45);
}
.field input:focus, .field select:focus {
	outline: none;
	border-color: var(--red-2);
	box-shadow: inset 0 2px 6px rgba(0,0,0,0.45), 0 0 0 1px rgba(200,16,46,0.30);
}

.league-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 300px;
	overflow: auto;
}
.league-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 12px 12px;
	background: linear-gradient(180deg, #141a2e, #0c1020);
	border: 1px solid var(--line);
	border-radius: var(--radius);
	text-align: left;
	width: 100%;
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
}
.league-row:hover {
	border-color: var(--red);
	box-shadow: inset 0 1px 0 rgba(255,255,255,0.04), var(--glow-red);
}
.league-row strong {
	display: block;
	font-family: var(--font-display);
	font-size: 18px;
	letter-spacing: 0.04em;
	text-transform: uppercase;
}
.league-row small { color: var(--text-3); }

@media (max-width: 1100px) {
	.grid-2, .grid-3, .stat-cards, .home-card-body {
		grid-template-columns: 1fr;
	}
	.workspace { grid-template-columns: 200px 1fr; }
}

/* ===== WBBL CRYPTO LEAGUE ===== */
.crypto-home { max-width: 1100px; }

.crypto-hero {
	display: grid;
	grid-template-columns: 1.2fr 0.9fr;
	gap: 18px;
	align-items: stretch;
}
.crypto-hero-copy h1 {
	font-family: var(--font-display);
	font-size: clamp(42px, 6vw, 64px);
	line-height: 0.95;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	margin: 0 0 12px;
}
.crypto-hero-copy h1 em {
	font-style: normal;
	color: var(--red-2);
	text-shadow: var(--glow-red);
}
.crypto-eyebrow {
	margin: 0 0 8px;
	color: var(--text-3);
	font-family: var(--font-ui);
	letter-spacing: 0.14em;
	text-transform: uppercase;
	font-size: 12px;
}
.crypto-lede {
	color: var(--text-2);
	max-width: 46ch;
	margin: 0 0 16px;
	line-height: 1.45;
}
.crypto-cta-row {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	margin-bottom: 10px;
}
.crypto-claim-panel { min-height: 220px; }

.crypto-team-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
	gap: 8px;
}
.crypto-team-chip {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 10px 12px;
	text-align: left;
	background: linear-gradient(180deg, #141a2e, #0c1020);
	border: 1px solid var(--line);
	border-radius: var(--radius);
	color: var(--text);
	transition: border-color 0.14s, box-shadow 0.14s;
}
.crypto-team-chip:hover {
	border-color: var(--red);
	box-shadow: 0 0 10px rgba(200,16,46,0.18);
}
.crypto-team-chip .abbrev {
	font-family: var(--font-ui);
	font-weight: 700;
	color: var(--red-2);
	letter-spacing: 0.06em;
}
.crypto-team-chip.claimed { border-color: rgba(200,16,46,0.4); }
.crypto-team-chip.mine { border-color: var(--navy-2); box-shadow: 0 0 10px rgba(0,51,153,0.25); }
.crypto-team-chip.mine .abbrev { color: var(--gold); }

/* ── WBBL stars stripe accent (hero header decoration) ── */
.wbbl-stripe {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 10px;
}
.wbbl-stripe-bar {
	height: 4px;
	border-radius: 2px;
}
.wbbl-stripe-bar.r { background: var(--red); flex: 2; }
.wbbl-stripe-bar.w { background: var(--white); flex: 1; }
.wbbl-stripe-bar.b { background: var(--navy-2); flex: 3; }

/* ── sync/live indicator ── */
.sync-dot {
	width: 8px; height: 8px;
	border-radius: 50%;
	background: var(--green);
	box-shadow: 0 0 6px rgba(47,214,123,0.55);
	display: inline-block;
}
.sync-dot.stale { background: var(--gold); box-shadow: 0 0 6px rgba(232,200,74,0.55); }
.sync-dot.offline { background: var(--red); box-shadow: 0 0 6px rgba(200,16,46,0.45); }

@media (max-width: 900px) {
	.crypto-hero { grid-template-columns: 1fr; }
}

/* ===== RESPONSIVE / MOBILE ===== */

/* Tablet */
@media (max-width: 900px) {
	:root { --sidebar-w: 190px; }
	.topbar { grid-template-columns: 180px 1fr auto; }
	.topbar-brand { font-size: 22px; }
	.topbar-league { font-size: 15px; letter-spacing: 0.06em; }
	.meta-chip:not(:first-child) { display: none; }
	.grid-2, .grid-3, .stat-cards, .home-card-body { grid-template-columns: 1fr; }
}

/* Mobile — sidebar becomes slide-in drawer */
@media (max-width: 640px) {
	:root {
		--top-h: 48px;
		--sidebar-w: 260px;
	}

	/* Topbar: just brand + hamburger, hide league title & chips */
	.topbar {
		grid-template-columns: auto 1fr auto;
		padding: 0 10px;
		gap: 8px;
	}
	.topbar-brand { font-size: 20px; gap: 5px; }
	.topbar-league { display: none; }
	.meta-chip { display: none !important; }

	/* Workspace: full-width */
	.workspace { grid-template-columns: 1fr; }

	/* Sidebar hides off left edge, slides in when .nav-open */
	.sidebar {
		position: fixed;
		top: var(--top-h);
		left: 0;
		bottom: 0;
		width: var(--sidebar-w);
		z-index: 100;
		transform: translateX(-100%);
		transition: transform 0.22s ease;
		box-shadow: 4px 0 24px rgba(0,0,0,0.75);
	}
	.sidebar.nav-open { transform: translateX(0); }

	/* Main takes full width with smaller padding */
	.main { padding: 14px 12px 28px; }

	/* Tables always scroll horizontally */
	.panel-body { overflow-x: auto; -webkit-overflow-scrolling: touch; }

	/* Stat cards: 2 columns on phone */
	.stat-cards { grid-template-columns: repeat(2, 1fr); gap: 8px; }
	.stat-card .value { font-size: 26px; }
	.page-title { font-size: 28px; }

	/* Home card */
	.home-card-header h1 { font-size: 36px; }
	.home-card-header, .home-card-body { padding: 16px; }
	.home-card-body { display: flex; flex-direction: column; gap: 16px; }

	/* Grids all single column */
	.grid-2, .grid-3 { grid-template-columns: 1fr; }

	/* Crypto hero */
	.crypto-hero { grid-template-columns: 1fr; }
	.crypto-team-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
}

/* Hamburger button: only shown on mobile */
.hamburger-btn { display: none; }

/* Scrim: hidden everywhere by default, shown only on mobile when nav is open */
.nav-scrim { display: none; }

@media (max-width: 640px) {
	.hamburger-btn {
		display: grid !important;
		place-items: center;
		width: 36px;
		height: 36px;
		flex-shrink: 0;
		border-radius: var(--radius);
		background: var(--steel-btn);
		border: 1px solid var(--line);
		color: var(--text-2);
	}
	.nav-scrim.nav-open {
		display: block;
		position: fixed;
		inset: 0;
		top: var(--top-h);
		background: rgba(0,0,0,0.6);
		backdrop-filter: blur(2px);
		z-index: 99;
	}
}
`;

export const injectDesktopStyles = () => {
	if (document.getElementById("court-desk-styles")) {
		return;
	}
	const el = document.createElement("style");
	el.id = "court-desk-styles";
	el.textContent = DESKTOP_CSS;
	document.head.appendChild(el);
};
